import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import confetti from "canvas-confetti";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Handshake, ArrowRight, Send, Download, Link2, Check, Facebook, MessageCircle, Instagram } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export function SponsorProposalDialog({ open, onClose }) {
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const contentRef = useRef(null);

  const downloadPdf = useCallback(async () => {
    if (!contentRef.current) return;
    setGenerating(true);
    try {
      const html2pdf = (await import("html2pdf.js")).default;
      const el = contentRef.current;
      const filename = `Derwent_CC_Sponsorship_Proposal_${p?.cover_year || "2025"}.pdf`;
      await html2pdf()
        .set({
          margin: 0,
          filename,
          image: { type: "jpeg", quality: 0.92 },
          html2canvas: { scale: 2, useCORS: true, logging: false, scrollY: 0 },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
          pagebreak: { mode: ["avoid-all", "css", "legacy"] },
        })
        .from(el)
        .save();
      toast.success("PDF downloaded");
    } catch (err) {
      console.error("PDF generation failed:", err);
      toast.error("Could not generate PDF — please try again");
    } finally {
      setGenerating(false);
    }
  }, [p]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/sponsorship-proposal");
        if (alive) setP(data);
      } catch {
        if (alive) setP({});
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open]);

  const tiers = p?.tiers || [];

  const copyLink = useCallback(async () => {
    const url = `${window.location.origin}/sponsor`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy — try manually: " + url);
    }
  }, []);

  const shareUrl = `${window.location.origin}/sponsor`;
  const shareText = `Check out the ${p?.cover_title || "Derwent Cricket Club Sponsorship Proposal"} — ${p?.cover_year || "2025"}`;

  const shareWhatsApp = useCallback(() => {
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText + "\n" + shareUrl)}`, "_blank", "noopener");
  }, [shareText, shareUrl]);

  const shareFacebook = useCallback(() => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, "_blank", "noopener,width=600,height=400");
  }, [shareUrl]);

  const shareInstagram = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.info("Link copied — paste it in your Instagram story or DM");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy — try manually: " + shareUrl);
    }
  }, [shareUrl]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="p-0 w-[96vw] max-w-4xl max-h-[92dvh] overflow-y-auto scrollbar-hide rounded-xl border-0 shadow-2xl bg-white text-foreground [&>button]:hidden"
        data-testid="sponsor-proposal-dialog"
      >
        <DialogHeader className="sr-only">
          <DialogTitle>Sponsorship Proposal</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="p-8 space-y-6">
            <Skeleton className="h-8 w-48 mx-auto" />
            <Skeleton className="h-64 w-full rounded-xl" />
            <Skeleton className="h-40 w-full rounded-xl" />
          </div>
        ) : (
          <div ref={contentRef} onClick={(e) => e.stopPropagation()}>
            {/* ============ COVER ============ */}
            <section className="relative bg-[hsl(var(--secondary))]/40" data-testid="dlg-proposal-cover">
              {p.cover_image_url && (
                <div className="relative aspect-[3/2] sm:aspect-[16/9] w-full overflow-hidden">
                  <img src={p.cover_image_url} alt="Sponsorship cover" className="absolute inset-0 w-full h-full object-cover" />
                </div>
              )}
              <div className="mx-auto max-w-3xl px-5 sm:px-8 py-8 sm:py-10 text-center">
                <div
                  className="inline-block px-6 py-2 rounded-full text-white text-lg sm:text-xl font-semibold mb-4"
                  style={{ background: "#4169E1" }}
                >
                  {p.cover_year || "2024/25"}
                </div>
                <h1
                  className="text-3xl sm:text-5xl lg:text-6xl uppercase tracking-tight leading-none"
                  style={{ fontFamily: "'Barlow Condensed', 'Inter', sans-serif", fontWeight: 900, color: "#1e3a8a" }}
                >
                  {p.cover_title || "Sponsorship Proposal"}
                </h1>
                {p.cover_intro && (
                  <p className="mt-5 max-w-2xl mx-auto text-sm sm:text-base text-foreground leading-relaxed">
                    {p.cover_intro}
                  </p>
                )}
                <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                  <Button
                    asChild
                    className="rounded-full bg-[hsl(var(--accent))] text-white hover:bg-[hsl(var(--accent))]/90 h-11 px-8 text-base"
                    data-testid="dlg-proposal-contact-btn"
                  >
                    <a href={`mailto:${p.team_email || "derwentcc@gmail.com"}`}>Enquire about sponsorship <ArrowRight className="ml-2 h-4 w-4" /></a>
                  </Button>
                  <Button
                    variant="outline"
                    className="rounded-full border-[hsl(var(--accent))]/30 text-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/5 h-11 px-6 text-base"
                    onClick={downloadPdf}
                    disabled={generating}
                    data-testid="dlg-proposal-download-pdf"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    {generating ? "Generating..." : "Download PDF"}
                  </Button>
                  <Button
                    variant="outline"
                    className="rounded-full border-[hsl(var(--accent))]/30 text-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/5 h-11 px-6 text-base"
                    onClick={copyLink}
                    data-testid="dlg-proposal-copy-link"
                  >
                    {copied ? <Check className="mr-2 h-4 w-4" /> : <Link2 className="mr-2 h-4 w-4" />}
                    {copied ? "Copied!" : "Copy link"}
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="rounded-full h-11 w-11 border-[#25D366]/30 text-[#25D366] hover:bg-[#25D366]/10"
                    onClick={shareWhatsApp}
                    data-testid="dlg-proposal-share-whatsapp"
                    title="Share via WhatsApp"
                  >
                    <MessageCircle className="h-5 w-5" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="rounded-full h-11 w-11 border-[#1877F2]/30 text-[#1877F2] hover:bg-[#1877F2]/10"
                    onClick={shareFacebook}
                    data-testid="dlg-proposal-share-facebook"
                    title="Share on Facebook"
                  >
                    <Facebook className="h-5 w-5" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="rounded-full h-11 w-11 border-[#E4405F]/30 text-[#E4405F] hover:bg-[#E4405F]/10"
                    onClick={shareInstagram}
                    data-testid="dlg-proposal-share-instagram"
                    title="Share on Instagram"
                  >
                    <Instagram className="h-5 w-5" />
                  </Button>
                </div>
              </div>
            </section>

            {/* ============ WHY SPONSOR ============ */}
            <section className="border-t border-border" data-testid="dlg-proposal-why">
              <div className="mx-auto max-w-3xl px-5 sm:px-8 py-8 sm:py-10 grid gap-6 lg:grid-cols-[1fr_280px] items-start">
                <div>
                  <ProposalHeading kicker="Why" title="Sponsor?" />
                  {p.why_intro && <p className="mt-4 text-sm sm:text-base leading-relaxed whitespace-pre-line">{p.why_intro}</p>}
                  {p.why_impact_intro && <p className="mt-4 text-sm sm:text-base leading-relaxed">{p.why_impact_intro}</p>}
                  {(p.why_bullets || []).length > 0 && (
                    <ul className="mt-3 space-y-2 text-sm sm:text-base list-disc pl-5">
                      {(p.why_bullets || []).map((b, i) => (
                        <li key={i} className="whitespace-pre-line leading-relaxed">{b}</li>
                      ))}
                    </ul>
                  )}
                  {p.why_footnote && <p className="mt-4 text-xs text-muted-foreground italic">{p.why_footnote}</p>}
                </div>
                {p.why_image_url && (
                  <div className="rounded-full overflow-hidden aspect-square border-4" style={{ borderColor: "#4169E1" }}>
                    <img src={p.why_image_url} alt="Why sponsor" className="w-full h-full object-cover" />
                  </div>
                )}
              </div>
            </section>

            {/* ============ HISTORY + TEAMS ============ */}
            <section className="border-t border-border bg-[hsl(var(--secondary))]/40" data-testid="dlg-proposal-history">
              <div className="mx-auto max-w-3xl px-5 sm:px-8 py-8 sm:py-10">
                <ProposalHeading kicker="Our" title="History" />
                {p.history_text && <p className="mt-4 text-sm sm:text-base whitespace-pre-line leading-relaxed">{p.history_text}</p>}
                {(p.teams_intro || (p.teams_image_urls || []).length > 0) && (
                  <div className="mt-8 grid gap-6 lg:grid-cols-[1fr_1fr] items-start">
                    <div>
                      <ProposalHeading kicker="Our" title="Teams" small />
                      {p.teams_intro && <p className="mt-3 text-sm sm:text-base whitespace-pre-line leading-relaxed font-semibold">{p.teams_intro}</p>}
                    </div>
                    {(p.teams_image_urls || []).length > 0 && (
                      <div className="grid grid-cols-2 gap-3">
                        {(p.teams_image_urls || []).slice(0, 4).map((u, i) => (
                          <div key={i} className="rounded-full aspect-square overflow-hidden border-4" style={{ borderColor: "#4169E1" }}>
                            <img src={u} alt={`Team ${i + 1}`} className="w-full h-full object-cover" />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </section>

            {/* ============ TIERS ============ */}
            {tiers.length > 0 && (
              <section className="border-t border-border" data-testid="dlg-proposal-tiers">
                <div className="mx-auto max-w-3xl px-5 sm:px-8 py-8 sm:py-10 space-y-10">
                  {tiers.map((t, i) => (
                    <TierBlock key={t.name || `tier-${i}`} tier={t} flip={i % 2 === 1} />
                  ))}
                </div>
              </section>
            )}

            {/* ============ MEET THE TEAM ============ */}
            {(p.team_intro || (p.team_members || []).length > 0) && (
              <section className="border-t border-border bg-[hsl(var(--secondary))]/40" data-testid="dlg-proposal-team">
                <div className="mx-auto max-w-3xl px-5 sm:px-8 py-8 sm:py-10 grid gap-6 lg:grid-cols-[1fr_1fr] items-start">
                  <div>
                    <ProposalHeading kicker="Meet The" title="Team" />
                    {p.team_intro && (
                      <p className="mt-4 text-sm sm:text-base whitespace-pre-line leading-relaxed">{p.team_intro}</p>
                    )}
                    {p.team_email && (
                      <a
                        href={`mailto:${p.team_email}`}
                        className="inline-block mt-4 text-lg sm:text-xl font-bold underline decoration-2 underline-offset-4"
                        style={{ color: "#1e3a8a" }}
                      >
                        {p.team_email}
                      </a>
                    )}
                  </div>
                  {(p.team_members || []).length > 0 && (
                    <div className="rounded-3xl p-5 sm:p-6" style={{ background: "#4169E1" }}>
                      <div className="grid grid-cols-2 gap-4 sm:gap-5">
                        {(p.team_members || []).map((m, i) => (
                          <div key={m.name || `member-${i}`} className="text-center">
                            <div className="aspect-square rounded-full overflow-hidden border-4 mx-auto max-w-[8rem]" style={{ borderColor: "#7aa2ff", background: "#e5edff" }}>
                              {m.image_url ? (
                                <img src={m.image_url} alt={m.name} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-white/60 text-xs uppercase">
                                  No photo
                                </div>
                              )}
                            </div>
                            <div className="mt-2 text-white font-bold text-sm sm:text-base leading-tight">{m.name}</div>
                            <div className="text-white/90 text-xs sm:text-sm leading-tight">{m.role}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* ============ SIGNAGE REFERENCE ============ */}
            {(p.signage_intro || p.signage_internal_image_url || p.signage_outdoor_image_url || tiers.length > 0) && (
              <section className="border-t border-border" data-testid="dlg-proposal-signage">
                <div className="mx-auto max-w-3xl px-5 sm:px-8 py-8 sm:py-10">
                  <ProposalHeading kicker="Sponsor" title="Signage" />
                  {p.signage_intro && (
                    <p className="mt-4 text-sm sm:text-base whitespace-pre-line leading-relaxed max-w-2xl">{p.signage_intro}</p>
                  )}
                  <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                    <div>
                      <div className="text-lg sm:text-xl font-bold mb-2">Internal Signage</div>
                      <div className="rounded-2xl border border-border overflow-hidden bg-white flex items-center justify-center min-h-[200px] p-4">
                        {p.signage_internal_image_url ? (
                          <img src={p.signage_internal_image_url} alt="Internal signage" className="max-w-full max-h-[360px] object-contain" />
                        ) : (
                          <SignageInternalSVG tiers={tiers} />
                        )}
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-2">850mm x 2000mm roll-up banner</div>
                    </div>
                    <div>
                      <div className="text-lg sm:text-xl font-bold mb-2">Outdoor Signage</div>
                      <div className="rounded-2xl border border-border overflow-hidden bg-white flex items-center justify-center min-h-[200px] p-4">
                        {p.signage_outdoor_image_url ? (
                          <img src={p.signage_outdoor_image_url} alt="Outdoor signage" className="max-w-full max-h-[360px] object-contain" />
                        ) : (
                          <SignageOutdoorSVG tiers={tiers} />
                        )}
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-2">2000mm x 900mm double-sided</div>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* ============ SPONSORSHIP FORM ============ */}
            <SponsorshipForm p={p} tiers={tiers} />

            {/* ============ BOTTOM CTA ============ */}
            <section className="border-t border-border bg-[hsl(var(--fg))] text-[hsl(var(--bg))]" data-testid="dlg-proposal-cta">
              <div className="mx-auto max-w-3xl px-5 sm:px-8 py-8 text-center">
                <h3
                  className="text-2xl sm:text-4xl uppercase tracking-tight leading-none"
                  style={{ fontFamily: "'Barlow Condensed', 'Inter', sans-serif", fontWeight: 900 }}
                >
                  Ready to join the Derwent family?
                </h3>
                <p className="mt-3 text-sm sm:text-base opacity-80">Reach out and we'll tailor a package to your business.</p>
                <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
                  <Button
                    asChild
                    className="rounded-full bg-white text-[hsl(var(--fg))] hover:bg-white/90 h-11 px-6"
                  >
                    <a href={`mailto:${p.team_email || "derwentcc@gmail.com"}`}>Email us</a>
                  </Button>
                  <Button
                    variant="outline"
                    className="rounded-full border-white/30 text-white hover:bg-white/10 h-11 px-6"
                    onClick={onClose}
                    data-testid="dlg-proposal-close-btn"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


/* ─── Sub-components ───────────────────────────────────── */

function ProposalHeading({ kicker, title, small }) {
  return (
    <div className="leading-none">
      <div
        className="text-xl sm:text-2xl italic text-[#4169E1] mb-1"
        style={{ fontFamily: "'Great Vibes', 'Barlow Condensed', cursive" }}
      >
        {kicker}
      </div>
      <div
        className={`${small ? "text-2xl sm:text-3xl" : "text-4xl sm:text-5xl"} uppercase tracking-tight`}
        style={{ fontFamily: "'Barlow Condensed', 'Inter', sans-serif", fontWeight: 900, color: "#1e3a8a" }}
      >
        {title}
      </div>
    </div>
  );
}

function TierBlock({ tier, flip }) {
  const photo = (
    <div className="shrink-0 w-32 sm:w-52 aspect-square rounded-full overflow-hidden border-4" style={{ borderColor: "#4169E1" }}>
      {tier.image_url ? (
        <img src={tier.image_url} alt={`${tier.name} sponsor`} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full bg-[hsl(var(--secondary))]" />
      )}
    </div>
  );
  const copy = (
    <div className="min-w-0 flex-1">
      <ProposalHeading kicker={tier.name} title="Sponsor" />
      <div className="mt-3 text-xl sm:text-2xl font-semibold" style={{ color: "#4169E1" }}>{tier.price}</div>
      {tier.term && <div className="text-xs sm:text-sm font-bold tracking-[0.15em] uppercase mt-1">{tier.term}</div>}
      <div className="mt-3 font-bold">Key Benefits</div>
      <ul className="mt-2 space-y-1.5 text-sm sm:text-base list-disc pl-5">
        {(tier.bullets || []).map((b, i) => <li key={i} className="leading-relaxed whitespace-pre-line">{b}</li>)}
      </ul>
    </div>
  );
  return (
    <div className={`flex flex-col ${flip ? "sm:flex-row-reverse" : "sm:flex-row"} items-start gap-5 sm:gap-8`}>
      {photo}
      {copy}
    </div>
  );
}


/* ─── Signage diagrams ─────────────────────────────────── */
const SIGNAGE_ORDER = ["gold", "silver", "bronze", "round", "player"];
function _tiersForSignage(tiers) {
  const map = new Map();
  (tiers || []).forEach((t) => {
    const name = (t.name || "").toLowerCase();
    let cat = null;
    if (name.startsWith("gold")) cat = "Gold";
    else if (name.startsWith("silver")) cat = "Silver";
    else if (name.startsWith("bronze")) cat = "Bronze";
    else if (name.startsWith("round")) cat = "Round";
    else if (name.includes("player")) cat = "Player";
    if (cat && !map.has(cat)) map.set(cat, true);
  });
  return ["Gold", "Silver", "Bronze", "Round", "Player"].filter((c) => map.has(c));
}

function SignageInternalSVG({ tiers }) {
  const cats = _tiersForSignage(tiers);
  const rowHeightByCat = { Gold: 80, Silver: 74, Bronze: 60, Round: 44, Player: 96 };
  const w = 180;
  const gap = 6;
  const rows = cats.map((c) => ({ cat: c, h: rowHeightByCat[c] || 40 }));
  const totalH = rows.reduce((s, r) => s + r.h + gap, 0) + 20;
  let y = 12;
  return (
    <svg viewBox={`0 0 ${w} ${totalH}`} className="w-full max-w-[200px] h-auto" aria-label="Internal signage layout">
      <rect x="0" y="0" width={w} height={totalH} rx="8" fill="#f8fafc" stroke="#cbd5e1" />
      <rect x="0" y="0" width={w} height="8" fill="#94a3b8" rx="4" />
      {rows.map((r, i) => {
        const cy = y;
        y += r.h + gap;
        if (r.cat === "Round") {
          const cellW = (w - 16 - gap) / 2;
          return (
            <g key={i}>
              <rect x="8" y={cy} width={cellW} height={r.h} rx="4" fill="#ffffff" stroke="#334155" />
              <text x={8 + cellW / 2} y={cy + r.h / 2 + 4} textAnchor="middle" fontSize="10" fontWeight="700" fill="#0f172a">Round</text>
              <rect x={8 + cellW + gap} y={cy} width={cellW} height={r.h} rx="4" fill="#ffffff" stroke="#334155" />
              <text x={8 + cellW + gap + cellW / 2} y={cy + r.h / 2 + 4} textAnchor="middle" fontSize="10" fontWeight="700" fill="#0f172a">Round</text>
            </g>
          );
        }
        if (r.cat === "Player") {
          const cols = 4;
          const rowsInPlayer = 2;
          const cellW = (w - 16 - gap * (cols - 1)) / cols;
          const cellH = (r.h - gap) / rowsInPlayer;
          const elements = [];
          for (let rr = 0; rr < rowsInPlayer; rr++) {
            for (let cc = 0; cc < cols; cc++) {
              const x = 8 + cc * (cellW + gap);
              const yy = cy + rr * (cellH + gap);
              elements.push(<rect key={`${rr}-${cc}`} x={x} y={yy} width={cellW} height={cellH} rx="3" fill="#ffffff" stroke="#334155" />);
            }
          }
          return (
            <g key={i}>
              {elements}
              <text x={w / 2} y={cy + r.h + 2} textAnchor="middle" fontSize="9" fontWeight="700" fill="#0f172a">Player Sponsors</text>
            </g>
          );
        }
        return (
          <g key={i}>
            <rect x="8" y={cy} width={w - 16} height={r.h} rx="4" fill="#ffffff" stroke="#334155" />
            <text x={w / 2} y={cy + r.h / 2 + 4} textAnchor="middle" fontSize="12" fontWeight="800" fill="#0f172a">{r.cat}</text>
          </g>
        );
      })}
    </svg>
  );
}

function SignageOutdoorSVG({ tiers }) {
  const cats = _tiersForSignage(tiers);
  const w = 280;
  const rowH = 60;
  const gap = 12;
  const rows = [];
  if (cats.includes("Gold")) rows.push({ cat: "Gold" });
  if (cats.includes("Silver")) rows.push({ cat: "Silver" });
  if (cats.includes("Bronze")) rows.push({ cat: "Bronze", split: 2 });
  if (cats.includes("Round") || cats.includes("Player")) rows.push({ cat: "Round-Player" });
  const totalH = rows.length * (rowH + gap);
  return (
    <svg viewBox={`0 0 ${w} ${totalH}`} className="w-full max-w-[280px] h-auto" aria-label="Outdoor signage layout">
      {rows.map((r, i) => {
        const y = i * (rowH + gap);
        if (r.split === 2) {
          const cellW = (w - 12 - gap) / 2;
          return (
            <g key={i}>
              <FenceRail x={6} y={y} w={cellW} h={rowH} label="Bronze" />
              <FenceRail x={6 + cellW + gap} y={y} w={cellW} h={rowH} label="Bronze" />
            </g>
          );
        }
        if (r.cat === "Round-Player") {
          const cellW = (w - 12 - gap) / 2;
          return (
            <g key={i}>
              <FenceRail x={6} y={y} w={cellW} h={rowH} label="Round" small />
              <FenceRail x={6 + cellW + gap} y={y} w={cellW} h={rowH} label="Player Sponsors" small />
            </g>
          );
        }
        return <FenceRail key={i} x={6} y={y} w={w - 12} h={rowH} label={r.cat} />;
      })}
    </svg>
  );
}

function FenceRail({ x, y, w, h, label, small }) {
  const railColor = "#0f172a";
  return (
    <g>
      <rect x={x} y={y + 2} width={w} height={4} rx="2" fill={railColor} />
      <rect x={x} y={y + h - 8} width={w} height={4} rx="2" fill={railColor} />
      <rect x={x} y={y} width={4} height={h} rx="2" fill={railColor} />
      <rect x={x + w - 4} y={y} width={4} height={h} rx="2" fill={railColor} />
      <rect x={x + 6} y={y + 8} width={w - 12} height={h - 18} fill="#ffffff" stroke="#334155" rx="3" />
      <text x={x + w / 2} y={y + h / 2 + (small ? 2 : 4)} textAnchor="middle" fontSize={small ? "10" : "14"} fontWeight="800" fill="#0f172a">{label}</text>
    </g>
  );
}


/* ─── Sponsorship form ─────────────────────────────────── */
const AUTOSAVE_KEY = "dcc_sponsor_form_draft";

function readDraft() {
  try { return JSON.parse(localStorage.getItem(AUTOSAVE_KEY)) || null; } catch { return null; }
}
function writeDraft(d) {
  try { localStorage.setItem(AUTOSAVE_KEY, JSON.stringify(d)); } catch {}
}
function clearDraft() {
  try { localStorage.removeItem(AUTOSAVE_KEY); } catch {}
}

function SponsorshipForm({ p, tiers }) {
  const tierOptions = useMemo(() => (tiers || []).map((t) => ({
    label: (t.name || "").toUpperCase() + (t.term ? ` (${t.term})` : ""),
    value: t.name,
    price: t.price || "",
  })), [tiers]);
  const extraOptions = (p.form_extra_options || []).map((label) => ({ label: label.toUpperCase(), value: label, price: "TBC" }));
  const allOptions = [...tierOptions, ...extraOptions];

  // Restore draft on mount
  const draft = useMemo(() => readDraft(), []);

  const [monetary, setMonetary] = useState(draft?.monetary ?? false);
  const [inKind, setInKind] = useState(draft?.inKind ?? false);
  const [inKindDetails, setInKindDetails] = useState(draft?.inKindDetails ?? "");
  const [selected, setSelected] = useState(draft?.selected ?? {});
  const [organisation, setOrganisation] = useState(draft?.organisation ?? "");
  const [contactName, setContactName] = useState(draft?.contactName ?? "");
  const [phone, setPhone] = useState(draft?.phone ?? "");
  const [email, setEmail] = useState(draft?.email ?? "");
  const [message, setMessage] = useState(draft?.message ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [restored, setRestored] = useState(!!draft);

  // Debounced autosave on any field change
  const saveTimer = useRef(null);
  useEffect(() => {
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      const hasContent = monetary || inKind || inKindDetails || Object.values(selected).some(Boolean) || organisation || contactName || phone || email || message;
      if (hasContent) {
        writeDraft({ monetary, inKind, inKindDetails, selected, organisation, contactName, phone, email, message });
      }
    }, 400);
    return () => clearTimeout(saveTimer.current);
  }, [monetary, inKind, inKindDetails, selected, organisation, contactName, phone, email, message]);

  const toggle = (v) => setSelected((prev) => ({ ...prev, [v]: !prev[v] }));

  const resetForm = () => {
    setMonetary(false); setInKind(false); setInKindDetails("");
    setSelected({}); setOrganisation(""); setContactName("");
    setPhone(""); setEmail(""); setMessage("");
    clearDraft();
    setRestored(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!organisation && !contactName && !email && !phone) {
      toast.error("Please add at least a name, organisation, phone or email so we can follow up");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/sponsorship-proposal/submit", {
        monetary,
        in_kind: inKind,
        in_kind_details: inKindDetails,
        selected_options: Object.keys(selected).filter((k) => selected[k]),
        organisation,
        contact_name: contactName,
        phone,
        email,
        message,
      });
      if (data.mailto_href) {
        confetti({ particleCount: 120, spread: 80, origin: { y: 0.6 } });
        window.open(data.mailto_href, "_self");
        toast.success(`Opening your email app to send this enquiry to ${data.return_email || "the club"}`);
      } else {
        confetti({ particleCount: 120, spread: 80, origin: { y: 0.6 } });
        toast.success(`Sent! We'll be in touch at ${email || phone || organisation}.`);
      }
      resetForm();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit — please try again");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="border-t border-border bg-[hsl(var(--secondary))]/40" data-testid="dlg-proposal-form">
      <div className="mx-auto max-w-3xl px-5 sm:px-8 py-8 sm:py-10">
        {(p.form_hero_image_urls || []).filter(Boolean).length > 0 && (
          <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-5">
            {(p.form_hero_image_urls || []).filter(Boolean).slice(0, 3).map((u, i) => (
              <div key={u || `hero-${i}`} className="aspect-[4/3] rounded-xl overflow-hidden">
                <img src={u} alt={`Team photo ${i + 1}`} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        )}
        <div className="text-center">
          <h2
            className="text-2xl sm:text-4xl uppercase tracking-tight leading-none"
            style={{ fontFamily: "'Barlow Condensed', 'Inter', sans-serif", fontWeight: 900, color: "#1e3a8a" }}
          >
            Club Sponsorship Form
          </h2>
          {p.form_intro && (
            <p className="mt-3 text-sm sm:text-base text-foreground max-w-2xl mx-auto whitespace-pre-line">{p.form_intro}</p>
          )}
        </div>

        <form onSubmit={submit} className="mt-6 space-y-5 rounded-2xl bg-background border border-border p-5 sm:p-6" data-testid="dlg-proposal-form-fields">
          {restored && (
            <div className="flex items-center justify-between gap-3 rounded-lg bg-[hsl(var(--accent))]/5 border border-[hsl(var(--accent))]/20 px-4 py-2.5" data-testid="dlg-draft-restored-banner">
              <span className="text-xs sm:text-sm text-[hsl(var(--accent))]">Draft restored from your last visit</span>
              <button type="button" onClick={resetForm} className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors underline underline-offset-2">Clear</button>
            </div>
          )}
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Checkbox id="dlg-monetary" checked={monetary} onCheckedChange={(v) => setMonetary(!!v)} />
              <Label htmlFor="dlg-monetary" className="text-sm sm:text-base leading-tight cursor-pointer">
                Yes! I would like to provide a monetary sponsorship to the club
              </Label>
            </div>
            <div className="flex items-start gap-3">
              <Checkbox id="dlg-in-kind" checked={inKind} onCheckedChange={(v) => setInKind(!!v)} />
              <Label htmlFor="dlg-in-kind" className="text-sm sm:text-base leading-tight cursor-pointer">
                I can provide in-kind support with vouchers or donations (please specify below)
              </Label>
            </div>
            {inKind && (
              <Textarea
                value={inKindDetails}
                onChange={(e) => setInKindDetails(e.target.value)}
                rows={3}
                placeholder="Describe the in-kind support you can provide"
              />
            )}
          </div>

          {allOptions.length > 0 && (
            <div>
              <div
                className="text-base sm:text-lg font-bold uppercase tracking-tight mb-3"
                style={{ color: "#1e3a8a", fontFamily: "'Barlow Condensed', 'Inter', sans-serif" }}
              >
                Please check appropriate sponsorship(s):
              </div>
              <div className="grid sm:grid-cols-2 gap-2 sm:gap-3">
                {allOptions.map((o, i) => (
                  <label
                    key={i}
                    className="flex items-center gap-3 rounded-lg border border-border bg-background hover:bg-[hsl(var(--secondary))]/50 px-3 py-2 cursor-pointer"
                  >
                    <Checkbox checked={!!selected[o.value]} onCheckedChange={() => toggle(o.value)} />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs sm:text-sm font-semibold leading-tight">{o.label}</div>
                    </div>
                    <div className="text-sm sm:text-base font-bold shrink-0" style={{ color: "#4169E1" }}>{o.price}</div>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div>
            <div
              className="text-base sm:text-lg font-bold uppercase tracking-tight mb-3"
              style={{ color: "#1e3a8a", fontFamily: "'Barlow Condensed', 'Inter', sans-serif" }}
            >
              Organisation Information
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium">Organisation</Label>
                <Input value={organisation} onChange={(e) => setOrganisation(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs font-medium">Contact name</Label>
                <Input value={contactName} onChange={(e) => setContactName(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs font-medium">Phone</Label>
                <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs font-medium">Email</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
            </div>
            <div className="mt-3">
              <Label className="text-xs font-medium">Message (optional)</Label>
              <Textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2 border-t border-border">
            <div className="text-xs sm:text-sm text-muted-foreground text-center sm:text-left">
              Send opens your email app pre-filled — press <span className="font-semibold text-foreground">Send</span> there to deliver to <span className="font-semibold text-foreground">{p.form_return_email || "derwentcc@gmail.com"}</span>
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="rounded-full bg-[hsl(var(--accent))] text-white hover:bg-[hsl(var(--accent))]/90 h-11 px-6"
              data-testid="dlg-proposal-submit-btn"
            >
              <Send className="h-4 w-4 mr-2" /> {submitting ? "Opening..." : "Send enquiry"}
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
}
